import { apiErrorMessage } from '@agos/api-client'
import {
  getGetAttendanceSelfTodayQueryKey,
  useGetAttendanceSelfToday,
  usePostAttendanceSelf,
} from '@agos/api-client/hr'
import { T, formatElapsed, formatTime, tt, useAuth } from '@agos/shared'
import { cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { LogIn, LogOut, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { phaseOf, type AttendanceRecord } from './attendance-phase'

/**
 * *Príchod* and *Odchod*, in the sidebar — `FRONTEND.md` §9.32, a port of legacy's
 * `components/sidebar-attendance-widget.tsx`.
 *
 * `/attendance` was ported and this was not, so the manager had the timesheet and no way to punch one.
 *
 * **The `self` endpoints, not the manager's.** `/attendance` is `@RequirePermission("attendance")`; a
 * cook, a driver and a waiter do not have it, cannot open that page, and are precisely the people who
 * clock in. `GET /attendance/self/today` and `POST /attendance/self` are `@RequireAuth` — your own row,
 * no permission — which is what makes a widget the right answer here rather than a link.
 */

/** How often the running shift's counter is redrawn. Legacy's interval. */
const TICK_MS = 30_000

export function SidebarAttendance() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [now, setNow] = useState(() => Date.now())

  const { data, isLoading } = useGetAttendanceSelfToday()
  const record = (data as { record?: AttendanceRecord | null } | undefined)?.record ?? null
  const phase = phaseOf(record)

  // Only while a shift is running: a ticking clock behind a screen that says *Pekný zvyšok dňa* is a
  // render every thirty seconds for a number nobody is reading.
  useEffect(() => {
    if (phase !== 'active') return undefined
    const timer = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(timer)
  }, [phase])

  const punch = usePostAttendanceSelf({
    mutation: {
      onSuccess: (answer) => {
        void queryClient.invalidateQueries({ queryKey: getGetAttendanceSelfTodayQueryKey() })
        const punched = (answer as { record?: AttendanceRecord } | undefined)?.record
        const arriving = phase === 'before'
        toast({
          title: arriving ? tt('Príchod zaznamenaný') : tt('Odchod zaznamenaný'),
          description: formatTime((arriving ? punched?.checkIn : punched?.checkOut) ?? new Date()),
        })
      },
      // A refusal is a 409 — already clocked in, already clocked out — and the backend says which.
      // Guessing at it here would put a second, possibly wrong explanation on the screen.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Chyba'),
          description: apiErrorMessage(caught, tt('Skúste to znova')),
        }),
    },
  })

  if (!user) return null

  const send = (event: 'check_in' | 'check_out') => punch.mutate({ data: { event } })
  const busy = punch.isPending

  return (
    <div data-sidebar-attendance={phase} className="px-2 pb-1">
      {(record?.checkIn || record?.checkOut) && (
        <div className="flex items-center gap-1.5 px-1 pb-1.5">
          {record?.checkIn && (
            <span
              data-attendance-in
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400"
            >
              <LogIn className="w-2.5 h-2.5" /> {formatTime(record.checkIn)}
            </span>
          )}
          {record?.checkOut && (
            <span
              data-attendance-out
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 text-blue-400"
            >
              <LogOut className="w-2.5 h-2.5" /> {formatTime(record.checkOut)}
            </span>
          )}
          {phase === 'active' && record?.checkIn && (
            <span className="text-[10px] font-bold text-sidebar-foreground/40 tabular-nums">
              {formatElapsed(record.checkIn, now)}
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="w-full py-2 flex items-center justify-center text-sidebar-foreground/40">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </div>
      ) : phase === 'finished' ? (
        <div className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-bold border border-blue-500/20">
          {/* The owner asked for the glyph to go: *"remove that emoji from there"*. The words are
            * the whole message. */}
          <T>Pekný zvyšok dňa</T>
        </div>
      ) : (
        <button
          type="button"
          data-attendance-punch={phase === 'before' ? 'check_in' : 'check_out'}
          disabled={busy}
          onClick={() => send(phase === 'before' ? 'check_in' : 'check_out')}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-white font-bold text-xs transition-all disabled:opacity-60',
            phase === 'before'
              ? 'bg-emerald-600 hover:bg-emerald-500'
              : 'bg-orange-600 hover:bg-orange-500',
          )}
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : phase === 'before' ? (
            <LogIn className="w-3.5 h-3.5" />
          ) : (
            <LogOut className="w-3.5 h-3.5" />
          )}
          {phase === 'before' ? <T>Príchod</T> : <T>Odchod</T>}
        </button>
      )}
    </div>
  )
}
